import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { eur, num, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Award, Plus, Pencil, Trash2, Gift, Ticket, Loader2 } from "lucide-react";

const EMPTY_REWARD = {
  name: "", description: "", points_required: 100, value_eur: 0,
  item_type: "service", item_ref_id: "", item_name: "",
  expiry_days: "", availability_qty: "", per_client_limit: "",
  stackable: true, required_service_id: "", required_service_name: "", active: true,
};

const VOUCHER_STATUS = {
  unused: { label: "Disponibile", token: "--hi-status-success" },
  used: { label: "Utilizzato", token: "--muted-foreground" },
  cancelled: { label: "Annullato", token: "--destructive" },
};

export default function Loyalty() {
  const { user } = useAuth();
  const canWrite = user && (user.permissions?.includes("*") || user.permissions?.includes("loyalty.write"));
  const [ov, setOv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);

  const loadCore = useCallback(() => {
    api.get("/loyalty/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data.loyalty)).catch(() => {});
  }, []);
  const loadRewards = useCallback(() => {
    api.get("/rewards").then((r) => setRewards(r.data)).catch(() => {});
    api.get("/rewards/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  const loadVouchers = useCallback(() => {
    api.get("/rewards/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    loadCore(); loadRewards(); loadVouchers();
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [loadCore, loadRewards, loadVouchers]);

  const saveSettings = async () => { await api.put("/loyalty/settings", settings); toast.success("Regole aggiornate"); };

  // --- Reward CRUD dialog ---
  const [rewardOpen, setRewardOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_REWARD);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const itemOptions = form.item_type === "product" ? products : services;
  const openCreate = () => { setEditingId(null); setForm(EMPTY_REWARD); setRewardOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || "", description: r.description || "",
      points_required: r.points_required ?? 0, value_eur: r.value_eur ?? 0,
      item_type: r.item_type || "service", item_ref_id: r.item_ref_id || "", item_name: r.item_name || "",
      expiry_days: r.expiry_days ?? "", availability_qty: r.availability_qty ?? "",
      per_client_limit: r.per_client_limit ?? "", stackable: r.stackable !== false,
      required_service_id: r.required_service_id || "", required_service_name: r.required_service_name || "",
      active: r.active !== false,
    });
    setRewardOpen(true);
  };
  const pickItem = (id) => {
    const it = itemOptions.find((x) => x.id === id);
    setForm((f) => ({ ...f, item_ref_id: id, item_name: it?.name || "" }));
  };
  const pickRequired = (id) => {
    const it = services.find((x) => x.id === id);
    setForm((f) => ({ ...f, required_service_id: id === "none" ? "" : id, required_service_name: id === "none" ? "" : (it?.name || "") }));
  };
  const saveReward = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome del premio"); return; }
    if (form.item_type !== "generic" && !form.item_ref_id) { toast.error("Collega un servizio o prodotto dal listino"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        points_required: Number(form.points_required) || 0,
        value_eur: Number(form.value_eur) || 0,
        expiry_days: form.expiry_days === "" ? null : Number(form.expiry_days),
        availability_qty: form.availability_qty === "" ? null : Number(form.availability_qty),
        per_client_limit: form.per_client_limit === "" ? null : Number(form.per_client_limit),
      };
      if (form.item_type === "generic") { payload.item_ref_id = null; payload.item_name = "Qualsiasi servizio"; }
      if (editingId) await api.put(`/rewards/${editingId}`, payload);
      else await api.post("/rewards", payload);
      toast.success(editingId ? "Premio aggiornato" : "Premio creato");
      setRewardOpen(false); loadRewards();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };
  const toggleActive = async (r) => {
    try { await api.put(`/rewards/${r.id}`, { active: !r.active }); loadRewards(); }
    catch { toast.error("Errore"); }
  };
  const doDelete = async () => {
    if (!delTarget) return;
    try { await api.delete(`/rewards/${delTarget.id}`); toast.success("Premio eliminato"); setDelTarget(null); loadRewards(); }
    catch { toast.error("Errore"); }
  };
  const cancelVoucher = async (v) => {
    try { await api.post(`/rewards/vouchers/${v.id}/cancel`); toast.success("Riscatto annullato, punti ripristinati"); loadVouchers(); loadRewards(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const summaryRows = summary?.rows || [];

  return (
    <div>
      <PageHeader title="Identity Rewards" subtitle="Programma fedeltà a punti e catalogo premi" />
      {ov && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard label="Punti emessi" value={num(ov.total_earned)} />
          <KpiCard label="Punti riscattati" value={num(ov.total_redeemed)} />
          <KpiCard label="Punti in circolazione" value={num(ov.outstanding)} />
          <KpiCard label="Valore premi erogati" value={eur(summary?.total_value_issued || 0)} />
        </div>
      )}

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog" data-testid="tab-catalog">Catalogo premi</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="tab-vouchers">Voucher</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Regole & Punti</TabsTrigger>
        </TabsList>

        {/* CATALOGO PREMI */}
        <TabsContent value="catalog" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">{summary?.active_rewards || 0} premi attivi · {summary?.total_redemptions || 0} riscatti totali</div>
            {canWrite && <Button onClick={openCreate} data-testid="reward-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo premio</Button>}
          </div>
          <Card className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Premio</TableHead>
                  <TableHead>Collegato a</TableHead>
                  <TableHead className="text-right">Punti</TableHead>
                  <TableHead className="text-right">Valore</TableHead>
                  <TableHead className="text-right">Riscatti</TableHead>
                  <TableHead className="text-right">Voucher disp.</TableHead>
                  <TableHead className="text-right">Erogato</TableHead>
                  <TableHead>Stato</TableHead>
                  {canWrite && <TableHead className="text-right">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaryRows.length === 0 && <TableRow><TableCell colSpan={canWrite ? 9 : 8} className="text-center text-muted-foreground py-10">Nessun premio nel catalogo. Crea il primo premio riscattabile.</TableCell></TableRow>}
                {summaryRows.map((r) => (
                  <TableRow key={r.id} data-testid={`reward-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.item_name || "—"}</TableCell>
                    <TableCell className="text-right tabular">{num(r.points_required)}</TableCell>
                    <TableCell className="text-right tabular">{eur(r.value_eur)}</TableCell>
                    <TableCell className="text-right tabular">{num(r.redemptions)}</TableCell>
                    <TableCell className="text-right tabular">{r.available == null ? "∞" : num(r.available)}</TableCell>
                    <TableCell className="text-right tabular">{eur(r.value_issued)}</TableCell>
                    <TableCell><Badge variant={r.active ? "default" : "secondary"}>{r.active ? "Attivo" : "Disattivo"}</Badge></TableCell>
                    {canWrite && (
                      <TableCell className="text-right whitespace-nowrap">
                        {(() => { const full = rewards.find((x) => x.id === r.id) || r; return (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(full)} data-testid={`reward-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Switch className="mx-1 align-middle" checked={!!r.active} onCheckedChange={() => toggleActive(full)} data-testid={`reward-toggle-${r.id}`} />
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelTarget(full)} data-testid={`reward-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </>
                        ); })()}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* VOUCHER */}
        <TabsContent value="vouchers" className="mt-4">
          <Card className="p-0 overflow-x-auto">
            <div className="p-4 border-b border-border flex items-center gap-2"><Ticket className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-xl font-semibold">Voucher riscattati</h3></div>
            <Table>
              <TableHeader><TableRow><TableHead>Codice</TableHead><TableHead>Premio</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Valore</TableHead><TableHead>Riscattato</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
              <TableBody>
                {vouchers.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nessun voucher riscattato.</TableCell></TableRow>}
                {vouchers.map((v) => {
                  const st = VOUCHER_STATUS[v.status] || VOUCHER_STATUS.unused;
                  return (
                    <TableRow key={v.id} data-testid={`voucher-row-${v.id}`}>
                      <TableCell className="font-mono text-sm">{v.code}</TableCell>
                      <TableCell>{v.reward_name}</TableCell>
                      <TableCell className="text-muted-foreground">{v.client_name}</TableCell>
                      <TableCell className="text-right tabular">{eur(v.value_eur)}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(v.redeemed_at)}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: `hsl(var(${st.token}))` }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: `hsl(var(${st.token}))` }} />{st.label}</span></TableCell>
                      <TableCell className="text-right">
                        {canWrite && v.status === "unused" && <Button variant="outline" size="sm" onClick={() => cancelVoucher(v)} data-testid={`voucher-cancel-${v.id}`}>Annulla</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* REGOLE */}
        <TabsContent value="rules" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5">
              <h3 className="font-heading text-xl font-semibold mb-4">Regole programma</h3>
              {settings && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Punti per € speso</Label><Input type="number" disabled={!canWrite} value={settings.points_per_euro} onChange={(e) => setSettings({ ...settings, points_per_euro: +e.target.value })} data-testid="loyalty-ppe" /></div>
                    <div className="space-y-1.5"><Label>Valore punto (€)</Label><Input type="number" step="0.01" disabled={!canWrite} value={settings.euro_per_point} onChange={(e) => setSettings({ ...settings, euro_per_point: +e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Scadenza (mesi)</Label><Input type="number" disabled={!canWrite} value={settings.expiry_months} onChange={(e) => setSettings({ ...settings, expiry_months: +e.target.value })} /></div>
                    <div className="space-y-1.5"><Label>Soglia riscatto</Label><Input type="number" disabled={!canWrite} value={settings.min_redeem} onChange={(e) => setSettings({ ...settings, min_redeem: +e.target.value })} /></div>
                  </div>
                  {canWrite && <Button onClick={saveSettings} data-testid="loyalty-save">Salva regole</Button>}
                </div>
              )}
            </Card>
            <Card className="p-0">
              <div className="p-4 border-b border-border flex items-center gap-2"><Award className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-xl font-semibold">Top clienti per punti</h3></div>
              <Table>
                <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead className="text-right">Punti</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(ov?.top_clients || []).map((c) => <TableRow key={c.client_id}><TableCell>{c.name}</TableCell><TableCell className="text-right font-medium tabular">{num(c.points)}</TableCell></TableRow>)}
                </TableBody>
              </Table>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reward create/edit dialog */}
      <Dialog open={rewardOpen} onOpenChange={setRewardOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto hi-scroll" data-testid="reward-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editingId ? "Modifica premio" : "Nuovo premio"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nome premio</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Piega omaggio" data-testid="reward-name" /></div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="reward-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tipo articolo</Label>
                <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v, item_ref_id: "", item_name: "" })}>
                  <SelectTrigger data-testid="reward-item-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="service">Servizio</SelectItem><SelectItem value="product">Prodotto</SelectItem><SelectItem value="generic">Voucher generico (qualsiasi servizio)</SelectItem></SelectContent>
                </Select>
              </div>
              {form.item_type === "generic" ? (
                <div className="space-y-1.5"><Label>Articolo collegato</Label>
                  <div className="h-10 flex items-center px-3 rounded-md border border-dashed border-border text-xs text-muted-foreground" data-testid="reward-generic-hint">Valido su qualsiasi servizio</div>
                </div>
              ) : (
              <div className="space-y-1.5"><Label>Articolo collegato</Label>
                <Select value={form.item_ref_id} onValueChange={pickItem}>
                  <SelectTrigger data-testid="reward-item-ref"><SelectValue placeholder="Dal listino" /></SelectTrigger>
                  <SelectContent>{itemOptions.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}{it.price != null ? ` · ${eur(it.price)}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Punti richiesti</Label><Input type="number" value={form.points_required} onChange={(e) => setForm({ ...form, points_required: e.target.value })} data-testid="reward-points" /></div>
              <div className="space-y-1.5"><Label>Valore sconto (€)</Label><Input type="number" step="0.01" value={form.value_eur} onChange={(e) => setForm({ ...form, value_eur: e.target.value })} data-testid="reward-value" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Scadenza (giorni)</Label><Input type="number" value={form.expiry_days} onChange={(e) => setForm({ ...form, expiry_days: e.target.value })} placeholder="∞" data-testid="reward-expiry" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Disponibilità</Label><Input type="number" value={form.availability_qty} onChange={(e) => setForm({ ...form, availability_qty: e.target.value })} placeholder="∞" data-testid="reward-availability" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Limite/cliente</Label><Input type="number" value={form.per_client_limit} onChange={(e) => setForm({ ...form, per_client_limit: e.target.value })} placeholder="∞" data-testid="reward-limit" /></div>
            </div>
            <div className="space-y-1.5"><Label>Servizio obbligatorio associato <span className="text-muted-foreground font-normal">(informativo)</span></Label>
              <Select value={form.required_service_id || "none"} onValueChange={pickRequired}>
                <SelectTrigger data-testid="reward-required-service"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Nessuno</SelectItem>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><div className="text-sm font-medium">Cumulabile</div><div className="text-xs text-muted-foreground">Più voucher applicabili sullo stesso scontrino</div></div>
              <Switch checked={form.stackable} onCheckedChange={(v) => setForm({ ...form, stackable: v })} data-testid="reward-stackable" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><div className="text-sm font-medium">Attivo</div><div className="text-xs text-muted-foreground">Visibile e riscattabile nel portale cliente</div></div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="reward-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRewardOpen(false)}>Annulla</Button>
            <Button onClick={saveReward} disabled={saving} data-testid="reward-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva premio"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il premio?</AlertDialogTitle>
            <AlertDialogDescription>"{delTarget?.name}" verrà rimosso dal catalogo. I voucher già riscattati restano validi nello storico.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="reward-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
